import React from 'react';
import { Helmet } from 'react-helmet-async';
import Navigation from '@/components/Navigation';
import LocalizedLink from '@/components/LocalizedLink';
import Footer from '@/components/Footer';
import LocaleSync from '@/components/LocaleSync';
import { SkipLinks } from '@/components/compliance/SkipLinks';
import CountUp from '@/components/motion/CountUp';
import CostMeter from '@/components/home/CostMeter';
import AmbientVideo from '@/components/motion/AmbientVideo';
import NeuralNetworkCanvas from '@/components/motion/NeuralNetworkCanvas';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { trackEvent } from '@/lib/analytics';
import { faqSchema, homePageSchema } from '@/lib/structured-data';
import { clientWork } from '@/data/clientWork';
import '../styles/light-studio.css';
import BookingLink, { BookingText } from '@/components/BookingLink';

const BRAND_FACTS = [
  {
    q: 'What is Brainfy AI?',
    a: 'Brainfy AI (also written Brainfy, spelled with a y) is a founder-led studio in Langley, British Columbia that builds AI-powered websites, chatbots, CRM automation and workflow automation for Canadian businesses, offers AI advisory and strategy work (audit and roadmap) for small businesses, and designs and operates its own AI software products, including real estate (proptech), compliance, agriculture and insurance tools.',
  },
  {
    q: 'Who runs Brainfy AI?',
    a: 'Brainfy AI is founded and run by Tilak Raj, who handles discovery, specifications, build and delivery on every engagement. Specialists are brought in for deep-domain work only when scope warrants and are disclosed in the statement of work.',
  },
  {
    q: 'Where does Brainfy AI work?',
    a: 'It is based in Langley, BC and works with businesses across Canada, mostly remotely, in English and French.',
  },
  {
    q: 'How is Brainfy AI priced?',
    a: 'Fixed-fee packages start at $1,997 CAD (taxes extra) and scale to $12,000+ for custom AI agents and workflow automation. The discovery call is free and there are no long-term contracts.',
  },
];

export default function LightStudioHome() {
  const reducedMotion = useReducedMotion();

  // Override dark theme from global index.css
  React.useEffect(() => {
    document.body.style.background = 'var(--bg-light)';
    document.body.style.color = 'var(--text-dark)';
    return () => {
      document.body.style.background = '';
      document.body.style.color = '';
    };
  }, []);

  // Scroll reveals for homepage sections. Progressive enhancement: the
  // .ls-reveal class (which hides content pre-reveal) is only ever added when
  // motion is allowed AND IntersectionObserver exists, so content can never be
  // stuck hidden for no-JS, reduced-motion, or legacy browsers.
  React.useEffect(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || typeof IntersectionObserver === 'undefined') return;

    const targets = Array.from(
      document.querySelectorAll('main .section > .container'),
    ).filter((el) => el.getBoundingClientRect().top > window.innerHeight * 0.9);

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -8% 0px' },
    );

    targets.forEach((el) => {
      el.classList.add('ls-reveal');
      io.observe(el);
    });

    // Safety net: never leave content hidden.
    const safety = window.setTimeout(() => {
      document.querySelectorAll('.ls-reveal').forEach((el) => el.classList.add('in'));
    }, 4000);

    return () => {
      io.disconnect();
      window.clearTimeout(safety);
    };
  }, []);

  // Subtle 3D tilt on ecosystem/product cards — fine pointers only, small
  // angles (max ~3.5deg). Inline transform includes the hover lift; clearing
  // it on leave hands control back to the CSS :hover rule.
  React.useEffect(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (reduce || !fine) return;

    const cards = Array.from(document.querySelectorAll<HTMLElement>('.card-grid .card'));
    const cleanups = cards.map((card) => {
      const onMove = (e: PointerEvent) => {
        const r = card.getBoundingClientRect();
        const px = (e.clientX - r.left) / r.width - 0.5;
        const py = (e.clientY - r.top) / r.height - 0.5;
        card.style.transform =
          `perspective(900px) rotateX(${(-py * 3.5).toFixed(2)}deg) rotateY(${(px * 3.5).toFixed(2)}deg) translateY(-4px)`;
      };
      const onLeave = () => {
        card.style.transform = '';
      };
      card.addEventListener('pointermove', onMove);
      card.addEventListener('pointerleave', onLeave);
      return () => {
        card.removeEventListener('pointermove', onMove);
        card.removeEventListener('pointerleave', onLeave);
        card.style.transform = '';
      };
    });
    return () => cleanups.forEach((fn) => fn());
  }, []);

  return (
    <>
      <LocaleSync />
      <Helmet>
        <title>Brainfy AI | AI Websites &amp; Business Automation, Canada</title>
        <meta name="description" content="Founder-led BC studio building AI-powered websites, chatbots, CRM and workflow automation for Canadian businesses. Fixed-fee packages from $1,997." />
        <meta name="keywords" content="AI Development Company Canada, Custom AI Solutions Canada, SaaS Development Canada, AI Consulting Canada, AI Automation Canada, Edmonton AI Company" />
        <link rel="canonical" href="https://www.brainfyai.com/" />
        {/* Organization + WebSite live statically in index.html (crawlable
            without JS). This adds a WebPage node that references them by @id. */}
        <script type="application/ld+json">{JSON.stringify(homePageSchema)}</script>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema(BRAND_FACTS.map((f) => ({ question: f.q, answer: f.a }))))}
        </script>
      </Helmet>

      <div className="pink-glow"></div>

      <div className="app-container">
        {/* Skip link + main landmark: every other route gets these from
            Layout.tsx, but the homepage bypasses Layout (ConditionalLayout),
            so it must provide its own for keyboard/screen-reader parity. */}
        <SkipLinks />
        {/* Shared site navigation (unified across all pages) */}
        <Navigation />

        <main id="main-content" tabIndex={-1}>
        {/* Hero */}
        <section className="hero">
          {/* Branded ambient background (Higgsfield-generated, reviewed).
              AmbientVideo handles reduced-motion, Save-Data/2G fallback,
              offscreen pausing, and the one-time video_play event.
              WebM 47KB / MP4 105KB + 8KB poster. */}
          {!reducedMotion && (
            <AmbientVideo
              className="hero-bg-video"
              srcWebm="/media/hero-bg.webm"
              srcMp4="/media/hero-bg.mp4"
              poster="/media/hero-bg-poster.jpg"
              analyticsLabel="homepage-hero-ambient"
            />
          )}
          <div className="container">
            <div className="hero-meta">
              <span><span className="dot"></span>Langley, BC · Building since 2023</span>
              <span>// AI Software · SaaS Products · Automation</span>
            </div>

            <h1 className="hero-title">
              We build <em className="grad-text">AI-powered SaaS, websites &amp; automation</em> real businesses run on.
            </h1>

            <p className="hero-subtitle">
              Brainfy AI designs, engineers and operates its own AI-powered
              SaaS products — and builds websites, chatbots, CRM automation
              and business workflows for Canadian businesses. Founder-led,
              fixed-fee, no long-term contracts.
            </p>

            <div className="hero-actions">
              <BookingLink
                location="homepage-hero"
                className="btn btn-primary"
                onClick={() =>
                  trackEvent('cta_click', {
                    cta_label: 'Book a Free Consultation',
                    cta_location: 'homepage-hero',
                  })
                }
              >
                <BookingText /> <span aria-hidden="true">→</span>
              </BookingLink>
              <a
                href="#products"
                className="btn btn-secondary"
                onClick={() =>
                  trackEvent('cta_click', {
                    cta_label: 'See products',
                    cta_location: 'homepage-hero',
                  })
                }
              >
                See products
              </a>
            </div>
            <a
              href="#cost-meter"
              className="hero-meter-link"
              onClick={() =>
                trackEvent('cta_click', {
                  cta_label: 'What is manual work costing you',
                  cta_location: 'homepage-hero',
                })
              }
            >
              What is manual work costing you? Try the 10-second meter ↓
            </a>

            {/* Proof Points */}
            <div className="hero-stats" style={{
              marginTop: '5rem',
              paddingTop: '3rem',
              borderTop: '1px solid var(--border)'
            }}>
              <div>
                <div style={{ fontSize: '2.5rem', fontWeight: 700, fontFamily: 'var(--mono)', marginBottom: '0.5rem' }}><CountUp value={9} /></div>
                <div style={{ color: 'var(--text-70)', fontSize: '0.9rem' }}>SaaS Products Built</div>
              </div>
              <div>
                <div style={{ fontSize: '2.5rem', fontWeight: 700, fontFamily: 'var(--mono)', marginBottom: '0.5rem' }}><CountUp value={13} suffix="+" /></div>
                <div style={{ color: 'var(--text-70)', fontSize: '0.9rem' }}>Years Engineering Experience</div>
              </div>
              <div>
                <div style={{ fontSize: '2.5rem', fontWeight: 700, fontFamily: 'var(--mono)', marginBottom: '0.5rem' }}><CountUp value={9} /></div>
                <div style={{ color: 'var(--text-70)', fontSize: '0.9rem' }}>Industries Our Products Serve</div>
              </div>
              <div>
                <div style={{ fontSize: '2.5rem', fontWeight: 700, fontFamily: 'var(--mono)', marginBottom: '0.5rem' }}>Langley</div>
                <div style={{ color: 'var(--text-70)', fontSize: '0.9rem' }}>Founder-led studio, BC, Canada</div>
              </div>
              <div>
                <div style={{ fontSize: '2.5rem', fontWeight: 700, fontFamily: 'var(--mono)', marginBottom: '0.5rem' }}><CountUp value={5} /></div>
                <div style={{ color: 'var(--text-70)', fontSize: '0.9rem' }}>Live in Production</div>
              </div>
              <div>
                <div style={{ fontSize: '2.5rem', fontWeight: 700, fontFamily: 'var(--mono)', marginBottom: '0.5rem' }}>Fixed-fee</div>
                <div style={{ color: 'var(--text-70)', fontSize: '0.9rem' }}>Scope and price agreed before we build</div>
              </div>
            </div>
          </div>
        </section>

        {/* Interactive cost meter: first conversion hook, right under the hero */}
        <CostMeter />

        {/* Technologies We Build With */}
        <section className="section" style={{ paddingTop: '4rem', paddingBottom: '4rem' }}>
          <div className="container">
            <div className="section-label" style={{ textAlign: 'center', marginBottom: '2.5rem' }}>// Technologies We Build With</div>
            <div className="tech-bar">
              {['OpenAI', 'Anthropic', 'Supabase', 'Vercel', 'Stripe', 'Google Gemini'].map((tech) => (
                <span key={tech} className="tech-bar__item">{tech}</span>
              ))}
            </div>
          </div>
        </section>

        {/* Trusted By */}
        <section className="section" style={{ paddingTop: '4rem', paddingBottom: '4rem' }}>
          <div className="container">
            <div className="section-label" style={{ textAlign: 'center', marginBottom: '2.5rem' }}>// Products We Build &amp; Operate</div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '2rem',
              alignItems: 'center',
              justifyItems: 'center'
            }}>
              <a href="https://www.realtordesk.ai/" target="_blank" rel="noopener noreferrer" style={{ fontWeight: 600, color: 'var(--text-70)' }}>RealtorDesk AI</a>
              <a href="https://www.canadacomplianceai.ca/" target="_blank" rel="noopener noreferrer" style={{ fontWeight: 600, color: 'var(--text-70)' }}>Canada Compliance AI</a>
              <a href="https://www.covioiq.com/" target="_blank" rel="noopener noreferrer" style={{ fontWeight: 600, color: 'var(--text-70)' }}>CovioIQ</a>
              <a href="https://www.agriintel.ca/" target="_blank" rel="noopener noreferrer" style={{ fontWeight: 600, color: 'var(--text-70)' }}>AgriIntel</a>
              <a href="https://www.petvitale.com/" target="_blank" rel="noopener noreferrer" style={{ fontWeight: 600, color: 'var(--text-70)' }}>PetVitale</a>
              <a href="https://www.indigenousrising.ai/" target="_blank" rel="noopener noreferrer" style={{ fontWeight: 600, color: 'var(--text-70)' }}>Indigenous Rising AI</a>
              <a href="https://www.navlyt.com/" target="_blank" rel="noopener noreferrer" style={{ fontWeight: 600, color: 'var(--text-70)' }}>Navlyt</a>
              <a href="https://www.buildersbridge.ca/" target="_blank" rel="noopener noreferrer" style={{ fontWeight: 600, color: 'var(--text-70)' }}>BuildersBridge</a>
              <a href="https://www.finovra-ai.com/" target="_blank" rel="noopener noreferrer" style={{ fontWeight: 600, color: 'var(--text-70)' }}>Finovra</a>
            </div>
          </div>
        </section>

        {/* Products */}
        <section className="section" id="products">
          <div className="container">
            <div className="section-label">// Our AI Product Ecosystem</div>
            <h2>AI products we design, build and operate</h2>
            <p className="section-intro">Read the story and status of each on our <LocalizedLink to="/products">AI products page</LocalizedLink>.</p>

            <div className="card-grid">
              <div className="card">
                <div className="product-card__header">
                  <div className="card-number">/01</div>
                  <span className="status-pill status-pill--beta">Beta</span>
                </div>
                <h3>RealtorDesk AI</h3>
                <div style={{ fontSize: '0.85rem', color: 'var(--text-50)', marginBottom: '0.75rem', fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Real Estate</div>
                <p style={{ marginBottom: '1rem' }}>Bilingual CRM for Canadian real estate agents: scores and triages new leads, keeps every conversation in one thread and shows deals in a pipeline. Data hosted in Canada; public beta with a 14-day free trial.</p>
                <div style={{ fontSize: '0.85rem', color: 'var(--text-50)', marginBottom: '0.5rem' }}><strong>Tech:</strong> React, Supabase, Anthropic Claude</div>
                <a href="https://www.realtordesk.ai/" target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.9rem', color: 'var(--text-dark)', textDecoration: 'underline' }}>View Live Product →</a>
              </div>

              <div className="card">
                <div className="product-card__header">
                  <div className="card-number">/02</div>
                  <span className="status-pill status-pill--beta">Beta</span>
                </div>
                <h3>Canada Compliance AI</h3>
                <div style={{ fontSize: '0.85rem', color: 'var(--text-50)', marginBottom: '0.75rem', fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Compliance</div>
                <p style={{ marginBottom: '1rem' }}>See where your business stands on CASL, PIPEDA and Quebec Law 25 in about two minutes, then work through a task plan, keep a breach register and export the records.</p>
                <div style={{ fontSize: '0.85rem', color: 'var(--text-50)', marginBottom: '0.5rem' }}><strong>Tech:</strong> React, Supabase, OpenAI &amp; Anthropic models</div>
                <a href="https://www.canadacomplianceai.ca/" target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.9rem', color: 'var(--text-dark)', textDecoration: 'underline' }}>View Live Product →</a>
              </div>

              <div className="card">
                <div className="product-card__header">
                  <div className="card-number">/03</div>
                  <span className="status-pill status-pill--live">Live</span>
                </div>
                <h3>CovioIQ</h3>
                <div style={{ fontSize: '0.85rem', color: 'var(--text-50)', marginBottom: '0.75rem', fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Insurance</div>
                <p style={{ marginBottom: '1rem' }}>Insurance broker CRM that flags renewals 90 days before expiry and scores each one for churn risk with the reasons named, so brokers know who to call first.</p>
                <div style={{ fontSize: '0.85rem', color: 'var(--text-50)', marginBottom: '0.5rem' }}><strong>Focus:</strong> Renewals &amp; client retention</div>
                <a href="https://www.covioiq.com/" target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.9rem', color: 'var(--text-dark)', textDecoration: 'underline' }}>View Live Product →</a>
              </div>

              <div className="card">
                <div className="product-card__header">
                  <div className="card-number">/04</div>
                  <span className="status-pill status-pill--live">Live</span>
                </div>
                <h3>PetVitale</h3>
                <div style={{ fontSize: '0.85rem', color: 'var(--text-50)', marginBottom: '0.75rem', fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Pet Healthcare</div>
                <p style={{ marginBottom: '1rem' }}>Medical records, vaccinations, medications and care reminders, organized for every pet in the household.</p>
                <div style={{ fontSize: '0.85rem', color: 'var(--text-50)', marginBottom: '0.5rem' }}><strong>Focus:</strong> Pet owners</div>
                <a href="https://www.petvitale.com/" target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.9rem', color: 'var(--text-dark)', textDecoration: 'underline' }}>View Live Product →</a>
              </div>

              <div className="card">
                <div className="product-card__header">
                  <div className="card-number">/05</div>
                  <span className="status-pill status-pill--live">Live</span>
                </div>
                <h3>AgriIntel</h3>
                <div style={{ fontSize: '0.85rem', color: 'var(--text-50)', marginBottom: '0.75rem', fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Agriculture</div>
                <p style={{ marginBottom: '1rem' }}>Hyperlocal weather, spray-window guidance and automated frost alerts for Canadian farms.</p>
                <div style={{ fontSize: '0.85rem', color: 'var(--text-50)', marginBottom: '0.5rem' }}><strong>Focus:</strong> Canadian farm operations</div>
                <a href="https://www.agriintel.ca/" target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.9rem', color: 'var(--text-dark)', textDecoration: 'underline' }}>View Live Product →</a>
              </div>

              <div className="card">
                <div className="product-card__header">
                  <div className="card-number">/06</div>
                  <span className="status-pill status-pill--beta">Beta</span>
                </div>
                <h3>Indigenous Rising AI</h3>
                <div style={{ fontSize: '0.85rem', color: 'var(--text-50)', marginBottom: '0.75rem', fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Indigenous Entrepreneurship</div>
                <p style={{ marginBottom: '1rem' }}>Find funding, build a business plan and access training in one place, designed around OCAP&reg; principles and community data sovereignty. Free to start; data stays in Canada.</p>
                <div style={{ fontSize: '0.85rem', color: 'var(--text-50)', marginBottom: '0.5rem' }}><strong>Tech:</strong> React, Supabase, OpenAI &amp; Anthropic models</div>
                <a href="https://www.indigenousrising.ai/" target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.9rem', color: 'var(--text-dark)', textDecoration: 'underline' }}>View Live Product →</a>
              </div>

              <div className="card">
                <div className="product-card__header">
                  <div className="card-number">/07</div>
                  <span className="status-pill status-pill--live">Live</span>
                </div>
                <h3>Navlyt</h3>
                <div style={{ fontSize: '0.85rem', color: 'var(--text-50)', marginBottom: '0.75rem', fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Aviation</div>
                <p style={{ marginBottom: '1rem' }}>Shows charter operators where their manuals fall short of FAA Part 135, quoting the exact clause behind every finding for a reviewer to accept or reject. EASA and Transport Canada coverage is on the roadmap.</p>
                <div style={{ fontSize: '0.85rem', color: 'var(--text-50)', marginBottom: '0.5rem' }}><strong>Tech:</strong> React, Supabase, OpenAI &amp; Anthropic models</div>
                <a href="https://www.navlyt.com" target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.9rem', color: 'var(--text-dark)', textDecoration: 'underline' }}>View Live Product →</a>
              </div>

              <div className="card">
                <div className="product-card__header">
                  <div className="card-number">/08</div>
                  <span className="status-pill status-pill--live">Live</span>
                </div>
                <h3>BuildersBridge</h3>
                <div style={{ fontSize: '0.85rem', color: 'var(--text-50)', marginBottom: '0.75rem', fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Construction</div>
                <p style={{ marginBottom: '1rem' }}>Run every job from lead to payment: leads, estimates, projects, crews, invoices and payments in one place, with Canadian sales tax built in.</p>
                <div style={{ fontSize: '0.85rem', color: 'var(--text-50)', marginBottom: '0.5rem' }}><strong>Tech:</strong> Next.js, Supabase, Anthropic Claude</div>
                <a href="https://www.buildersbridge.ca" target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.9rem', color: 'var(--text-dark)', textDecoration: 'underline' }}>View Live Product →</a>
              </div>

              <div className="card">
                <div className="product-card__header">
                  <div className="card-number">/09</div>
                  <span className="status-pill status-pill--beta">Beta</span>
                </div>
                <h3>Finovra</h3>
                <div style={{ fontSize: '0.85rem', color: 'var(--text-50)', marginBottom: '0.75rem', fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Accounting &amp; Finance</div>
                <p style={{ marginBottom: '1rem' }}>AI-native accounting for the US and Canada: ask your books a question and see the ledger entries behind every answer. AI drafts the work; a person approves what posts.</p>
                <div style={{ fontSize: '0.85rem', color: 'var(--text-50)', marginBottom: '0.5rem' }}><strong>Focus:</strong> Finance teams, US &amp; Canada</div>
                <a href="https://www.finovra-ai.com/" target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.9rem', color: 'var(--text-dark)', textDecoration: 'underline' }}>View Live Product →</a>
              </div>
            </div>

            <div style={{ textAlign: 'center', marginTop: '3rem' }}>
              <BookingLink location="homepage-products" className="btn btn-primary" />
            </div>
          </div>
        </section>

        {/* Recent Client Work — delivered websites, kept separate from owned products */}
        <section className="section" id="client-work" style={{ background: 'var(--bg-white)' }}>
          <div className="container">
            <div className="section-label">// Recent Client Work</div>
            <h2>Websites we&apos;ve built for real businesses.</h2>
            <p className="section-intro">
              Beyond our own products, we design and build conversion-focused
              websites for Canadian businesses — each one live and fully client-owned.
            </p>

            <div className="card-grid">
              {clientWork.map((w) => (
                <div className="card" key={w.slug}>
                  <a
                    href={w.liveUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Open the live ${w.client} website (opens in a new tab)`}
                    style={{ display: 'block', overflow: 'hidden', borderRadius: '0.5rem', marginBottom: '1rem', aspectRatio: '16 / 9', border: '1px solid rgba(0,0,0,0.08)' }}
                  >
                    <img
                      src={w.image}
                      alt={w.imageAlt}
                      width={640}
                      height={360}
                      loading="lazy"
                      style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', display: 'block' }}
                    />
                  </a>
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-50)', marginBottom: '0.5rem', fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                    {w.industryTag} · {w.location}
                  </div>
                  <h3>{w.client}</h3>
                  <p style={{ marginBottom: '1rem' }}>{w.summary}</p>
                  <a href={w.liveUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.9rem', color: 'var(--text-dark)', textDecoration: 'underline' }}>
                    View live site →
                  </a>
                </div>
              ))}
            </div>

            <div style={{ textAlign: 'center', marginTop: '3rem' }}>
              <LocalizedLink to="/work" className="btn btn-primary">View all client work →</LocalizedLink>
            </div>
          </div>
        </section>

        {/* Services */}
        <section className="section" id="services">
          <div className="container">
            <div className="section-label">What We Build</div>
            <h2>AI-powered websites &amp; automation that win customers</h2>
            <p className="section-intro">
              From your website to your follow-up — we build and connect the AI systems that capture, qualify, and convert leads for Canadian businesses. We also build <LocalizedLink to="/services/ai-voice-agents">AI voice agents</LocalizedLink>.
            </p>

            <div className="card-grid">
              <LocalizedLink className="card" to="/services/ai-websites">
                <div className="card-number">/01</div>
                <h3>AI Websites</h3>
                <p>Conversion-focused websites with AI built in — fast, mobile, and SEO-ready.</p>
              </LocalizedLink>

              <LocalizedLink className="card" to="/services/ai-chatbots">
                <div className="card-number">/02</div>
                <h3>AI Chatbots &amp; Lead Capture</h3>
                <p>24/7 assistants that answer questions, qualify leads, and book appointments.</p>
              </LocalizedLink>

              <LocalizedLink className="card" to="/services/crm-automation">
                <div className="card-number">/03</div>
                <h3>CRM Automation</h3>
                <p>Centralize leads and automate follow-up so no opportunity slips through.</p>
              </LocalizedLink>

              <LocalizedLink className="card" to="/services/workflow-automation">
                <div className="card-number">/04</div>
                <h3>Workflow Automation</h3>
                <p>Automate the busywork — documents, data entry, notifications, and approvals.</p>
              </LocalizedLink>

              <LocalizedLink className="card" to="/services/ai-audit">
                <div className="card-number">/05</div>
                <h3>AI Audit &amp; Strategy</h3>
                <p>Not sure where to start? A free audit to find your highest-ROI opportunities.</p>
              </LocalizedLink>
            </div>

            <div style={{ textAlign: 'center', marginTop: '3rem' }}>
              <BookingLink location="homepage-section" className="btn btn-primary" />
            </div>
          </div>
        </section>

        {/* Why Brainfy */}
        <section className="section" style={{ background: 'var(--bg-white)' }}>
          <div className="container">
            <div className="section-label">Why Companies Choose Brainfy</div>
            <h2>Founder-led AI development you can trust</h2>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '2rem',
              marginTop: '3rem'
            }}>
              <div>
                <h3 style={{ fontSize: '1.2rem', fontWeight: 700, marginBottom: '0.5rem' }}>✓ We Build Our Own Products</h3>
                <p style={{ color: 'var(--text-70)' }}>We run 7+ of our own production SaaS platforms — we ship and operate real products, not slide decks.</p>
              </div>
              <div>
                <h3 style={{ fontSize: '1.2rem', fontWeight: 700, marginBottom: '0.5rem' }}>✓ Canadian Focus</h3>
                <p style={{ color: 'var(--text-70)' }}>Built for Canadian businesses with PIPEDA, CASL, and AODA alignment from day one.</p>
              </div>
              <div>
                <h3 style={{ fontSize: '1.2rem', fontWeight: 700, marginBottom: '0.5rem' }}>✓ Founder-Led</h3>
                <p style={{ color: 'var(--text-70)' }}>Direct access to the founding engineer on every project — no account managers in between.</p>
              </div>
              <div>
                <h3 style={{ fontSize: '1.2rem', fontWeight: 700, marginBottom: '0.5rem' }}>✓ Scoped Delivery</h3>
                <p style={{ color: 'var(--text-70)' }}>Timelines depend on scope and are agreed in writing at kickoff. We ship in stages rather than promising a fixed number of weeks up front.</p>
              </div>
              <div>
                <h3 style={{ fontSize: '1.2rem', fontWeight: 700, marginBottom: '0.5rem' }}>✓ AI-First</h3>
                <p style={{ color: 'var(--text-70)' }}>AI embedded from day one — automation, agents, and custom workflows, not bolted-on afterthoughts.</p>
              </div>
              <div>
                <h3 style={{ fontSize: '1.2rem', fontWeight: 700, marginBottom: '0.5rem' }}>✓ Long-Term Partnership</h3>
                <p style={{ color: 'var(--text-70)' }}>Post-launch support, maintenance, and feature expansion — we operate what we build.</p>
              </div>
            </div>

            <div style={{ textAlign: 'center', marginTop: '3rem' }}>
              <a href="#contact" className="btn btn-primary">Start Your Project</a>
            </div>
          </div>
        </section>

        {/* Buying checklist — deliberately makes no claims about other providers */}
        <section className="section">
          <div className="container">
            <div className="section-label">// Before You Hire Anyone</div>
            <h2>Six questions to ask any provider — including us</h2>
            <p className="section-intro">
              Whoever you work with, these questions separate a scoped, accountable build from an open-ended one. Here is how we answer each.
            </p>

            <div className="card-grid" style={{ marginTop: '2.5rem' }}>
              <div className="card">
                <h3>How is the work priced?</h3>
                <p>Fixed-fee packages from $1,997 CAD, published on our <LocalizedLink to="/pricing" style={{ textDecoration: 'underline' }}>pricing page</LocalizedLink>. Optional care plans run month to month.</p>
              </div>
              <div className="card">
                <h3>Who actually builds it?</h3>
                <p>We are a founder-led studio: the founder scopes each project and stays involved through delivery. Based in Langley, BC. We also work with Edmonton businesses: <LocalizedLink to="/ai-consulting-edmonton" style={{ textDecoration: 'underline' }}>AI consulting and web automation in Edmonton</LocalizedLink>.</p>
              </div>
              <div className="card">
                <h3>Who owns the result?</h3>
                <p>Ownership of code, domain, content and accounts should be written into the agreement. Ask to see those terms before you sign, with us or anyone else.</p>
              </div>
              <div className="card">
                <h3>What happens after launch?</h3>
                <p>Optional care plans start at $99/month with no long-term contract. Ongoing support is scoped and priced separately from the build.</p>
              </div>
              <div className="card">
                <h3>Can you show working examples?</h3>
                <p>Yes. Our own products are linked above and delivered client websites are on our <LocalizedLink to="/work" style={{ textDecoration: 'underline' }}>work page</LocalizedLink>, with templates and concept sites labelled as such.</p>
              </div>
              <div className="card">
                <h3>How are timelines set?</h3>
                <p>By scope. We agree the timeline in writing at kickoff instead of promising a fixed number of weeks up front.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Case Studies */}
        <section className="section" id="case-studies" style={{ background: 'var(--bg-white)' }}>
          <div className="container">
            <div className="section-label">Case Studies</div>
            <h2>What we've built, and what it does today</h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '3rem', marginTop: '3rem' }}>
              {/* RealtorDesk AI */}
              <div className="card" style={{ padding: '3rem' }}>
                <h3 style={{ fontSize: '2rem', marginBottom: '1.5rem' }}>RealtorDesk AI</h3>

                <div style={{ display: 'grid', gap: '2rem' }}>
                  <div>
                    <h4 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.75rem', color: 'var(--text-dark)' }}>Challenge</h4>
                    <p style={{ color: 'var(--text-70)', lineHeight: 1.7 }}>
                      Canadian real estate agents were manually tracking leads across spreadsheets, email, and sticky notes. No centralized system for follow-up automation or market insights.
                    </p>
                  </div>

                  <div>
                    <h4 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.75rem', color: 'var(--text-dark)' }}>Solution</h4>
                    <p style={{ color: 'var(--text-70)', lineHeight: 1.7 }}>
                      Built an AI-powered CRM that responds to new leads immediately and captures lead details for follow-up, with Claude AI powering the conversations.
                    </p>
                  </div>

                  <div>
                    <h4 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.75rem', color: 'var(--text-dark)' }}>What it does today</h4>
                    <ul style={{ color: 'var(--text-70)', lineHeight: 1.7, paddingLeft: '1.5rem' }}>
                      <li>Answers new leads instantly, in English or French</li>
                      <li>Captures lead details and scores them for follow-up</li>
                      <li>14-day free trial (public beta)</li>
                      <li>Designed with PIPEDA in mind (no compliance certification is claimed)</li>
                    </ul>
                  </div>
                </div>

                <a href="https://www.realtordesk.ai/" target="_blank" rel="noopener noreferrer" className="btn btn-primary" style={{ marginTop: '2rem' }}>Visit RealtorDesk AI →</a>
              </div>

              {/* Canada Compliance AI */}
              <div className="card" style={{ padding: '3rem' }}>
                <h3 style={{ fontSize: '2rem', marginBottom: '1.5rem' }}>Canada Compliance AI</h3>

                <div style={{ display: 'grid', gap: '2rem' }}>
                  <div>
                    <h4 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.75rem', color: 'var(--text-dark)' }}>Challenge</h4>
                    <p style={{ color: 'var(--text-70)', lineHeight: 1.7 }}>
                      Canadian SMBs struggled to stay compliant with PIPEDA, CASL, and AODA regulations. Manual audits were expensive and time-consuming.
                    </p>
                  </div>

                  <div>
                    <h4 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.75rem', color: 'var(--text-dark)' }}>Solution</h4>
                    <p style={{ color: 'var(--text-70)', lineHeight: 1.7 }}>
                      Developed a compliance platform for Canadian SMEs that starts with a short self-assessment and organizes CASL, PIPEDA, Law 25 and AODA obligations, using OpenAI and Anthropic models for analysis.
                    </p>
                  </div>

                  <div>
                    <h4 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.75rem', color: 'var(--text-dark)' }}>What it does today</h4>
                    <ul style={{ color: 'var(--text-70)', lineHeight: 1.7, paddingLeft: '1.5rem' }}>
                      <li>Free 8-question CASL / PIPEDA / Law 25 assessment (about 2 minutes)</li>
                      <li>Platform covering CASL, PIPEDA, Quebec Law 25 and AODA</li>
                      <li>Canadian data residency (hosted in Montreal)</li>
                      <li>Free to start, no signup required for the assessment</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* CovioIQ */}
              <div className="card" style={{ padding: '3rem' }}>
                <h3 style={{ fontSize: '2rem', marginBottom: '1.5rem' }}>CovioIQ</h3>

                <div style={{ display: 'grid', gap: '2rem' }}>
                  <div>
                    <h4 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.75rem', color: 'var(--text-dark)' }}>Challenge</h4>
                    <p style={{ color: 'var(--text-70)', lineHeight: 1.7 }}>
                      Canadian insurance brokers manually tracked quotes, policies, and renewals across multiple systems. High risk of missed renewals and policy lapses.
                    </p>
                  </div>

                  <div>
                    <h4 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.75rem', color: 'var(--text-dark)' }}>Solution</h4>
                    <p style={{ color: 'var(--text-70)', lineHeight: 1.7 }}>
                      Built a CRM for insurance brokers centred on renewals: automated 90/60/30-day reminders and churn-risk prediction meant to flag at-risk clients early.
                    </p>
                  </div>

                  <div>
                    <h4 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.75rem', color: 'var(--text-dark)' }}>What it does today</h4>
                    <ul style={{ color: 'var(--text-70)', lineHeight: 1.7, paddingLeft: '1.5rem' }}>
                      <li>Automated renewal reminders at 90, 60 and 30 days</li>
                      <li>Churn-risk prediction for at-risk clients</li>
                      <li>Live, with a 14-day free trial</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ textAlign: 'center', marginTop: '3rem' }}>
              <a href="#contact" className="btn btn-primary">Start Your Project</a>
            </div>
          </div>
        </section>

        {/* Industries */}
        <section className="section" id="industries" style={{ background: 'var(--bg-white)' }}>
          <div className="container">
            <div className="section-label">Industries We Serve</div>
            <h2>Domain expertise across verticals</h2>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '2rem',
              marginTop: '3rem'
            }}>
              <div>
                <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>🏢</div>
                <h3 style={{ fontSize: '1.3rem', fontWeight: 700, marginBottom: '0.5rem' }}>Real Estate</h3>
                <p style={{ color: 'var(--text-70)' }}>CRMs, lead management, market analytics for brokerages and agents.</p>
              </div>

              <div>
                <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>🛡️</div>
                <h3 style={{ fontSize: '1.3rem', fontWeight: 700, marginBottom: '0.5rem' }}>Insurance</h3>
                <p style={{ color: 'var(--text-70)' }}>Renewal reminders and churn-risk tracking for Canadian brokers.</p>
              </div>

              <div>
                <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>🌾</div>
                <h3 style={{ fontSize: '1.3rem', fontWeight: 700, marginBottom: '0.5rem' }}>Agriculture</h3>
                <p style={{ color: 'var(--text-70)' }}>Hyperlocal weather, spray-window guidance and frost alerts.</p>
              </div>

              <div>
                <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>🏥</div>
                <h3 style={{ fontSize: '1.3rem', fontWeight: 700, marginBottom: '0.5rem' }}>Healthcare</h3>
                <p style={{ color: 'var(--text-70)' }}>Pet health records and reminders for pet owners.</p>
              </div>

              <div>
                <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>✈️</div>
                <h3 style={{ fontSize: '1.3rem', fontWeight: 700, marginBottom: '0.5rem' }}>Aviation</h3>
                <p style={{ color: 'var(--text-70)' }}>Compliance, operations, and workflow tools for charter aviation operators.</p>
              </div>

              <div>
                <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>⚖️</div>
                <h3 style={{ fontSize: '1.3rem', fontWeight: 700, marginBottom: '0.5rem' }}>Compliance</h3>
                <p style={{ color: 'var(--text-70)' }}>PIPEDA, CASL, AODA automation for Canadian businesses.</p>
              </div>

              <div>
                <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>🏛️</div>
                <h3 style={{ fontSize: '1.3rem', fontWeight: 700, marginBottom: '0.5rem' }}>Public Sector</h3>
                <p style={{ color: 'var(--text-70)' }}>Indigenous economic development and community tools.</p>
              </div>
            </div>

            <div style={{ textAlign: 'center', marginTop: '3rem' }}>
              <BookingLink location="homepage-section" className="btn btn-primary" />
            </div>
          </div>
        </section>

        {/* Free Tools — lead magnets */}
        <section className="section">
          <div className="container">
            <div className="section-label">Free Tools</div>
            <h2>Try us before you talk to us</h2>
            <p className="section-intro">
              Two quick, no-pressure tools to see where AI fits your business — no sales call required.
            </p>

            <div className="card-grid">
              <LocalizedLink className="card" to="/ai-readiness">
                <div className="card-number">/01</div>
                <h3>AI Readiness Assessment</h3>
                <p>Answer 10 quick questions and get an instant readiness score plus a tailored PDF report.</p>
                <span style={{ color: 'var(--text-dark)', textDecoration: 'underline', fontSize: '0.9rem' }}>Take the assessment →</span>
              </LocalizedLink>

              <LocalizedLink className="card" to="/roi-calculator">
                <div className="card-number">/02</div>
                <h3>Automation ROI Calculator</h3>
                <p>Plug in your numbers and see what repetitive manual work is costing you each year — and how much automation could reclaim.</p>
                <span style={{ color: 'var(--text-dark)', textDecoration: 'underline', fontSize: '0.9rem' }}>Calculate your ROI →</span>
              </LocalizedLink>
            </div>
          </div>
        </section>

        {/* Brand definition: visible entity facts (also emitted as FAQPage JSON-LD) */}
        <section className="section" id="about-brainfy" style={{ background: 'var(--bg-white)' }}>
          <div className="container">
            <div className="section-label">// About Brainfy AI</div>
            <h2>Brainfy AI at a glance</h2>
            <p className="section-intro">
              The short answers, with links to the detail. See the <LocalizedLink to="/about">about page</LocalizedLink>,{' '}
              <LocalizedLink to="/pricing">pricing</LocalizedLink> and{' '}
              <LocalizedLink to="/products">AI products</LocalizedLink>.
            </p>
            <div className="card-grid">
              {BRAND_FACTS.map((f) => (
                <div className="card" key={f.q}>
                  <h3>{f.q}</h3>
                  <p>{f.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="section" id="process">
          <div className="container">
            <div className="section-label">Our Process</div>
            <h2>From strategy to production</h2>

            <div className="card-grid">
              <div className="card">
                <div className="card-number">Phase 1</div>
                <h3>Discovery & Strategy</h3>
                <p>We map your workflows, identify AI opportunities, and design the right solution architecture. Fixed-fee scoping included.</p>
              </div>

              <div className="card">
                <div className="card-number">Phase 2</div>
                <h3>Build & Test</h3>
                <p>Rapid prototyping, iterative development, rigorous testing with real data. Weekly progress updates.</p>
              </div>

              <div className="card">
                <div className="card-number">Phase 3</div>
                <h3>Deploy & Monitor</h3>
                <p>Production launch with observability, cost tracking, and performance monitoring, plus a privacy and consent check with open questions flagged for your own adviser.</p>
              </div>

              <div className="card">
                <div className="card-number">Phase 4</div>
                <h3>Scale & Optimize</h3>
                <p>Continuous improvement, feature expansion, model fine-tuning. Ongoing support packages available.</p>
              </div>
            </div>

            <div style={{ textAlign: 'center', marginTop: '3rem' }}>
              <a href="#contact" className="btn btn-primary">Start Your Project</a>
            </div>
          </div>
        </section>

        {/* Tech Stack */}
        <section className="section" id="stack" style={{ background: 'var(--bg-white)' }}>
          <div className="container">
            <div className="section-label">Technology Stack</div>
            <h2>The tools our products run on</h2>

            <div className="card-grid">
              <div className="card">
                <h3>AI & ML</h3>
                <p>Anthropic Claude, OpenAI GPT-4o, Google Gemini</p>
              </div>

              <div className="card">
                <h3>Frontend</h3>
                <p>React, Next.js, TypeScript, Tailwind CSS, Vite</p>
              </div>

              <div className="card">
                <h3>Backend</h3>
                <p>Supabase (PostgreSQL, Auth, Edge Functions), Next.js, Stripe</p>
              </div>

              <div className="card">
                <h3>Infrastructure</h3>
                <p>Vercel, GitHub Actions, Sentry</p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact CTA — interactive neural-network backdrop (brand blues);
            static single frame under reduced motion, paused offscreen. */}
        <section className="section" id="contact" style={{ position: 'relative', overflow: 'hidden' }}>
          <NeuralNetworkCanvas opacity={0.4} />
          <div className="container" style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ textAlign: 'center', maxWidth: '700px', margin: '0 auto' }}>
              <h2 style={{ marginBottom: '1.5rem' }}>Ready to build?</h2>
              <p className="section-intro" style={{ margin: '0 auto 2.5rem' }}>
                Book a free 30-minute AI audit. We'll review your workflows and identify the highest-ROI AI opportunities. No sales pitch, just strategic insights.
              </p>
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                <BookingLink location="homepage-contact" className="btn btn-primary" style={{ fontSize: '1.05rem', padding: '1rem 2.5rem' }}>
                  <BookingText />
                  <span className="arrow" aria-hidden="true">→</span>
                </BookingLink>
                <a href="#case-studies" className="btn btn-secondary" style={{ fontSize: '1.05rem', padding: '1rem 2.5rem' }}>
                  See Case Studies
                </a>
              </div>
            </div>
          </div>
        </section>
        </main>

      </div>

      {/* Shared site footer (unified across all pages) */}
      <Footer />
    </>
  );
}
